import { useEffect, useMemo, useState } from "react";
import {
  Mic, Gift, Users, MessageCircle, Wine, ClipboardList, Camera, Video, PartyPopper,
  ArrowRight, MapPin, Calendar, Send, Instagram, Check, Star, Sparkles, Globe,
} from "lucide-react";
import { TICKETS, GALLERY, HERO_IMG, PARTNERS, SPEAKER_PHOTOS } from "../lib/content";
import RegistrationModal from "../components/RegistrationModal";
import ContactForm from "../components/ContactForm";
import PaymentReturnHandler from "../components/PaymentReturnHandler";
import ThankYouModal from "../components/ThankYouModal";
import { useCatalog, ticketPrice, ticketCurrency } from "../lib/useCatalog";
import { useT, LANGS } from "../lib/i18n";

const PROGRAM_ICONS = [Mic, Gift, Users, MessageCircle, Wine, ClipboardList, Camera, Video, PartyPopper];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Landing() {
  const { t } = useT();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState("standard");
  const [thankYouOpen, setThankYouOpen] = useState(false);

  const handlePaymentResult = (state) => {
    if (state === "paid") setThankYouOpen(true);
  };

  const openTicket = (ticketKey) => {
    setSelectedTicket(ticketKey);
    setModalOpen(true);
  };

  useEffect(() => {
    const brand = t("nav.brand") || "Business Conference";
    const date = t("hero.date_chip");
    document.title = date && date !== "hero.date_chip" ? `${brand} · ${date}` : brand;
  }, [t]);

  return (
    <div className="min-h-screen bg-[#F8F5F2] text-[#1A1A1A] relative">
      <PaymentReturnHandler onResult={handlePaymentResult} />
      <Header onPick={() => scrollToId("pricing")} />
      <Hero onCta={() => scrollToId("pricing")} />
      <Program />
      <Speakers />
      <Organizers />
      <Atmosphere />
      <Pricing onPick={openTicket} />
      <Partners />
      <ContactForm />
      <FAQ />
      <Footer />
      <RegistrationModal open={modalOpen} onOpenChange={setModalOpen} defaultTicket={selectedTicket} />
      <ThankYouModal open={thankYouOpen} onOpenChange={setThankYouOpen} />
    </div>
  );
}

function LanguageSwitcher({ variant = "header" }) {
  const { lang, setLang } = useT();
  return (
    <div
      data-testid="language-switcher"
      className={`inline-flex items-center gap-1 border px-1 py-1 ${variant === "header" ? "border-[#E2D8CF] bg-[#F8F5F2]/70" : "border-[#F8F5F2]/30 bg-transparent"}`}
    >
      <Globe className={`w-3.5 h-3.5 mx-1 ${variant === "header" ? "text-ink2" : "text-[#F8F5F2]/70"}`} />
      {LANGS.map((l) => {
        const active = l.code === lang;
        return (
          <button
            key={l.code}
            onClick={() => setLang(l.code)}
            data-testid={`lang-${l.code}`}
            aria-pressed={active}
            className={`px-2 py-1 text-[11px] tracking-widest font-bold uppercase transition-colors ${
              active
                ? (variant === "header" ? "bg-[#6E1A24] text-[#F8F5F2]" : "bg-[#F8F5F2] text-[#1A1A1A]")
                : (variant === "header" ? "text-ink2 hover:text-[#6E1A24]" : "text-[#F8F5F2]/70 hover:text-[#F8F5F2]")
            }`}
          >
            {l.label}
          </button>
        );
      })}
    </div>
  );
}

function Header({ onPick }) {
  const { t } = useT();
  return (
    <header data-testid="site-header" className="sticky top-0 z-40 backdrop-blur-xl bg-[#F8F5F2]/85 border-b border-[#E2D8CF]">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 md:py-4 flex items-center justify-between gap-3">
        <a href="#top" className="flex items-center gap-2" data-testid="brand-link">
          <span className="inline-block w-8 h-8 bg-[#6E1A24] text-[#F8F5F2] grid place-items-center font-display text-lg">B</span>
          <span className="font-display uppercase tracking-widest text-xs md:text-sm">{t("nav.brand") || "Business Conf · Warsaw"}</span>
        </a>
        <nav className="hidden md:flex items-center gap-8 text-sm uppercase tracking-widest text-ink2">
          <a href="#program" className="hover:text-[#6E1A24] transition-colors" data-testid="nav-program">{t("nav.program")}</a>
          <a href="#speakers" className="hover:text-[#6E1A24] transition-colors" data-testid="nav-speakers">{t("nav.speakers")}</a>
          <a href="#gallery" className="hover:text-[#6E1A24] transition-colors" data-testid="nav-gallery">{t("nav.gallery")}</a>
          <a href="#pricing" className="hover:text-[#6E1A24] transition-colors" data-testid="nav-pricing">{t("nav.pricing")}</a>
          <a href="#contact" className="hover:text-[#6E1A24] transition-colors" data-testid="nav-contact">{t("nav.contact") || "Contact"}</a>
        </nav>
        <div className="flex items-center gap-2 md:gap-3">
          <LanguageSwitcher />
          <button onClick={onPick} data-testid="header-cta" className="btn-primary px-4 md:px-5 py-2.5 text-[11px] md:text-xs whitespace-nowrap">
            {t("nav.buy")}
          </button>
        </div>
      </div>
    </header>
  );
}

function Hero({ onCta }) {
  const { t } = useT();
  return (
    <section id="top" className="relative overflow-hidden grain">
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-14 md:pb-24">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="flex items-center gap-3 mb-6">
              <span className="kbd">{t("hero.date_chip")}</span>
              <span className="text-xs uppercase tracking-widest text-ink2">{t("hero.date_sub")}</span>
            </div>
            <h1 className="heading-poster text-[14vw] sm:text-[10vw] md:text-[7.5vw] lg:text-[6.5rem] leading-[0.9]">
              <span className="block">{t("hero.title1")}</span>
              <span className="block text-[#6E1A24]">{t("hero.title2")}</span>
            </h1>
            <p className="mt-6 md:mt-8 max-w-xl text-ink2 text-base md:text-lg leading-relaxed">{t("hero.desc")}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button onClick={onCta} data-testid="hero-cta-tickets" className="btn-primary px-7 py-4 inline-flex items-center gap-2">
                {t("hero.cta_tickets")} <ArrowRight className="w-4 h-4" />
              </button>
              <a href="#program" data-testid="hero-cta-program" className="btn-outline px-7 py-4 inline-flex items-center gap-2">
                {t("hero.cta_program")}
              </a>
            </div>

            <div className="mt-10 md:mt-14 grid grid-cols-3 gap-6 max-w-md">
              <Stat k={t("hero.stat1k")} v={t("hero.stat1v")} />
              <Stat k={t("hero.stat2k")} v={t("hero.stat2v")} />
              <Stat k={t("hero.stat3k")} v={t("hero.stat3v")} />
            </div>
          </div>

          <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative">
              <div className="absolute -inset-3 bg-[#6E1A24] -z-10 translate-x-3 translate-y-3" />
              <img src={HERO_IMG} alt="Speaker on stage" className="w-full aspect-[4/5] object-cover" data-testid="hero-image" />
              <div className="absolute bottom-0 left-0 right-0 bg-[#1A1A1A] text-[#F8F5F2] p-4 flex items-center justify-between">
                <div>
                  <div className="eyebrow text-[#F8F5F2]/60">{t("hero.stage_kicker")}</div>
                  <div className="font-display uppercase text-lg leading-tight">{t("hero.stage_title")}</div>
                </div>
                <Sparkles className="w-5 h-5 text-[#F8F5F2]" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ k, v }) {
  if (!k || k === "hero.stat1k" || typeof k !== "string" || !k.trim()) return null;
  return (
    <div>
      <div className="font-display text-3xl md:text-4xl text-[#6E1A24]">{k}</div>
      <div className="text-xs uppercase tracking-widest text-ink2 mt-1 leading-snug">{v}</div>
    </div>
  );
}

function Program() {
  const { t } = useT();
  const items = t("program.items") || [];
  return (
    <section id="program" className="relative py-20 md:py-28 bg-[#F2EBE5] border-y border-[#E2D8CF]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="eyebrow text-[#6E1A24] mb-3">{t("program.eyebrow")}</div>
            <h2 className="heading-poster text-4xl sm:text-5xl md:text-6xl">{t("program.title")}</h2>
          </div>
          <p className="max-w-md text-ink2">{t("program.desc")}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {items.map((p, i) => {
            const Icon = PROGRAM_ICONS[i] || Mic;
            return (
              <div key={i} data-testid={`program-card-${i}`} className="card-soft p-6 md:p-8 group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#6E1A24] text-[#F8F5F2] grid place-items-center shrink-0 group-hover:scale-105 transition-transform">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-display uppercase text-lg leading-tight">{p.title}</h3>
                    <p className="mt-2 text-ink2 text-sm leading-relaxed">{p.text}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Speakers() {
  const { t } = useT();
  const list = t("speakers.list") || [];
  const roleLabel = t("speakers.role_label");
  return (
    <section id="speakers" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="eyebrow text-[#6E1A24] mb-3">{t("speakers.eyebrow")}</div>
            <h2 className="heading-poster text-4xl sm:text-5xl md:text-6xl">{t("speakers.title")}</h2>
          </div>
          <p className="max-w-md text-ink2">{t("speakers.desc")}</p>
        </div>

        {list.length === 0 ? (
          <div data-testid="speakers-tba" className="p-8 md:p-12 border border-[#E2D8CF] bg-[#F2EBE5] text-center max-w-2xl mx-auto">
            <div className="w-12 h-12 bg-[#6E1A24] text-[#F8F5F2] grid place-items-center mx-auto mb-4">
              <Mic className="w-6 h-6" />
            </div>
            <h3 className="font-display uppercase text-2xl md:text-3xl text-[#1A1A1A]">
              {t("speakers.tba_title") || "Speakers will be announced soon"}
            </h3>
            <p className="mt-3 text-ink2 text-base leading-relaxed">
              {t("speakers.tba_desc") || "Stay tuned for updates on our lineup!"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {list.map((s, i) => (
              <article key={i} data-testid={`speaker-card-${i}`} className="speaker-card border border-[#E2D8CF] bg-[#F2EBE5]">
                <div className="relative aspect-[3/4] bg-[#EAE0D8] overflow-hidden">
                  <img src={SPEAKER_PHOTOS[i]} alt={s.name} className="photo w-full h-full object-cover" />
                  <div className="overlay" />
                  <div className="teaser">
                    <div className="eyebrow opacity-80">{roleLabel}</div>
                    <div className="font-display uppercase text-lg leading-tight">{s.teaser}</div>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-display uppercase text-xl leading-tight">{s.name}</h3>
                  <p className="text-ink2 text-sm mt-1">{s.role}</p>
                  {s.link && (
                    <a href={s.link} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-sm text-[#6E1A24] underline underline-offset-2">
                      {s.link.replace(/^https?:\/\//, "")}
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Organizers() {
  const { t } = useT();
  const organizers = t("organizers.list") || [];
  return (
    <section id="organizers" className="py-20 md:py-28 bg-[#F2EBE5] border-y border-[#E2D8CF]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <div className="eyebrow text-[#6E1A24] mb-3">{t("organizers.eyebrow")}</div>
          <h2 className="heading-poster text-4xl sm:text-5xl md:text-6xl">{t("organizers.title")}</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {organizers.map((org, i) => (
            <div key={i} className="text-center">
              <div className="relative aspect-[3/4] bg-[#EAE0D8] overflow-hidden mb-4 max-w-[280px] mx-auto">
                <img src={org.photo} alt={org.name} className="w-full h-full object-cover" />
              </div>
              <h3 className="font-display uppercase text-xl leading-tight">{org.name}</h3>
              <p className="text-ink2 text-sm mt-1">{org.role}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Atmosphere() {
  const { t } = useT();
  return (
    <section id="gallery" className="py-20 md:py-28 bg-[#1A1A1A] text-[#F8F5F2]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="eyebrow text-[#F8F5F2]/60 mb-3">{t("atmosphere.eyebrow")}</div>
            <h2 className="heading-poster text-4xl sm:text-5xl md:text-6xl">{t("atmosphere.title")}</h2>
          </div>
          <p className="max-w-md text-[#F8F5F2]/70">{t("atmosphere.desc")}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {GALLERY.slice(0, 6).map((src, i) => (
            <div key={i} data-testid={`gallery-image-${i}`} className={`relative overflow-hidden ${i === 0 ? "row-span-2 col-span-2 md:col-span-2 md:row-span-2 aspect-[4/3] md:aspect-auto" : "aspect-[4/3]"}`}>
              <img src={src} alt="Past event" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing({ onPick }) {
  const { t } = useT();
  const { catalog } = useCatalog();
  return (
    <section id="pricing" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <div className="eyebrow text-[#6E1A24] mb-3">{t("pricing.eyebrow")}</div>
          <h2 className="heading-poster text-4xl sm:text-5xl md:text-6xl">{t("pricing.title")}</h2>
          <p className="text-ink2 mt-4">{t("pricing.desc")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {TICKETS.filter((meta) => !meta.hidden).map((meta) => (
            <TicketCard key={meta.key} meta={meta} catalog={catalog} onPick={onPick} />
          ))}
        </div>

        <p className="text-center text-xs text-ink2 mt-10 max-w-md mx-auto">{t("pricing.footer")}</p>
      </div>
    </section>
  );
}

function TicketCard({ meta, catalog, onPick }) {
  const { t } = useT();
  const trans = t(`pricing.tickets.${meta.key}`) || {};
  const highlighted = meta.highlight;
  const soldOut = meta.soldOut;
  const badgeText = meta.badgeKey ? t(`pricing.badges.${meta.badgeKey}`) : null;
  // Faza 2B (2.1): cena WYŁĄCZNIE z backend catalog; przed załadowaniem pokaż placeholder.
  const price = ticketPrice(catalog, meta.key);
  const currency = ticketCurrency(catalog, meta.key);

  return (
    <div
      data-testid={`ticket-card-${meta.key}`}
      className={`relative flex flex-col p-7 md:p-8 transition-transform overflow-hidden ${
        soldOut ? "opacity-75" : "hover:-translate-y-1"
      } ${
        highlighted ? "bg-[#6E1A24] text-[#F8F5F2] border-none shadow-xl" : "bg-[#F2EBE5] text-[#1A1A1A] border border-[#E2D8CF]"
      }`}
    >
      {soldOut && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="text-[#6E1A24] font-display text-4xl md:text-5xl uppercase tracking-widest opacity-30 -rotate-[25deg] select-none">
            SOLD OUT
          </div>
        </div>
      )}
      {badgeText && (
        <div data-testid={`ticket-badge-${meta.key}`} className={`absolute -top-3 left-7 text-[10px] tracking-widest uppercase font-bold px-3 py-1 ${highlighted ? "bg-[#F8F5F2] text-[#6E1A24]" : "bg-[#1A1A1A] text-[#F8F5F2]"}`}>
          {badgeText}
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{meta.icon}</span>
        {trans.sub && <span className={`text-[10px] uppercase tracking-widest ${highlighted ? "text-[#F8F5F2]/70" : "text-ink2"}`}>{trans.sub}</span>}
      </div>
      <h3 className="font-display uppercase text-2xl leading-tight">{trans.name || meta.key}</h3>
      <div className="mt-5 flex items-baseline gap-2">
        <span data-testid={`ticket-price-${meta.key}`} className="font-display text-5xl md:text-6xl leading-none">{price === null ? "—" : price}</span>
        <span className={`text-sm uppercase tracking-widest ${highlighted ? "text-[#F8F5F2]/70" : "text-ink2"}`}>{currency}</span>
      </div>
      <ul className="mt-6 space-y-3">
        {(trans.perks || []).map((p, i) => (
          <li key={i} className="flex items-start gap-2 text-sm leading-relaxed">
            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${highlighted ? "text-[#F8F5F2]" : "text-[#6E1A24]"}`} />
            <span className={highlighted ? "text-[#F8F5F2]/90" : "text-ink2"}>{p}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={() => {
          if (soldOut) return;
          onPick(meta.key);
        }}
        disabled={soldOut}
        data-testid={`ticket-cta-${meta.key}`}
        className={`mt-7 inline-flex items-center justify-center gap-2 px-6 py-3.5 uppercase tracking-widest text-sm font-bold transition-colors ${
          soldOut ? "bg-gray-400 text-gray-600 cursor-not-allowed" :
          highlighted ? "bg-[#F8F5F2] text-[#6E1A24] hover:bg-white" : "bg-[#1A1A1A] text-[#F8F5F2] hover:bg-[#6E1A24]"
        }`}
      >
        {soldOut ? "SOLD OUT" : (trans.cta || "→")} {!soldOut && <ArrowRight className="w-4 h-4" />}
      </button>
    </div>
  );
}

function Partners() {
  const { t } = useT();
  const list = useMemo(() => [...PARTNERS, ...PARTNERS], []);
  return (
    <section id="partners" className="py-16 md:py-20 bg-[#F2EBE5] border-y border-[#E2D8CF]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-8">
          <div className="eyebrow text-[#6E1A24] mb-3">{t("partners.eyebrow")}</div>
          <h2 className="heading-poster text-3xl sm:text-4xl">{t("partners.title")}</h2>
        </div>
        <div className="marquee">
          <div className="marquee-track flex gap-12 animate-marquee" data-testid="partners-marquee">
            {list.map((p, i) => (
              <div key={i} className="flex items-center gap-3 px-6 py-4 border border-[#E2D8CF] bg-[#F8F5F2]">
                <Star className="w-4 h-4 text-[#6E1A24]" />
                <span className="font-display uppercase tracking-widest text-lg">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const { t } = useT();
  const items = t("faq.items") || [];
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <div className="eyebrow text-[#6E1A24] mb-3">{t("faq.eyebrow")}</div>
          <h2 className="heading-poster text-4xl md:text-5xl">{t("faq.title")}</h2>
        </div>
        <div className="divide-y divide-[#E2D8CF] border-y border-[#E2D8CF]">
          {items.map((it, i) => (
            <details key={i} data-testid={`faq-item-${i}`} className="group py-5">
              <summary className="cursor-pointer flex items-center justify-between gap-4">
                <span className="font-display uppercase text-lg">{it.q}</span>
                <span className="text-[#6E1A24] font-display text-2xl group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-ink2 leading-relaxed">{it.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useT();
  return (
    <footer className="bg-[#1A1A1A] text-[#F8F5F2] pt-16 pb-10">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-3 gap-10 mb-12">
          <div>
            <div className="font-display uppercase text-3xl">{t("footer.brand") || "Business Conference"}</div>
            <p className="text-[#F8F5F2]/70 mt-3 text-sm">{t("footer.sub")}</p>
            <div className="flex items-center gap-3 mt-5">
              <a href="https://t.me/yepgt" target="_blank" rel="noreferrer" data-testid="footer-telegram" className="w-10 h-10 grid place-items-center border border-[#F8F5F2]/30 hover:bg-[#6E1A24] hover:border-[#6E1A24] transition-colors">
                <Send className="w-4 h-4" />
              </a>
              <a href="https://www.instagram.com/connect_elevate.network" target="_blank" rel="noreferrer" data-testid="footer-instagram" className="w-10 h-10 grid place-items-center border border-[#F8F5F2]/30 hover:bg-[#6E1A24] hover:border-[#6E1A24] transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              <div className="ml-2"><LanguageSwitcher variant="footer" /></div>
            </div>
          </div>

          <div>
            <div className="eyebrow text-[#F8F5F2]/60 mb-3">{t("footer.contacts")}</div>
            <ul className="space-y-2 text-sm text-[#F8F5F2]/85">
              <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#6E1A24]" /> {t("footer.contact_location") || "Warsaw, Poland"}</li>
              <li className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#6E1A24]" /> {t("footer.contact_date") || "21.06 · 14:00"}</li>
              <li className="flex items-center gap-2"><Send className="w-4 h-4 text-[#6E1A24]" /> {t("footer.contact_tg") || "@bizconf_warsaw"}</li>
            </ul>
          </div>

          <div>
            <div className="eyebrow text-[#F8F5F2]/60 mb-3">{t("footer.docs")}</div>
            <ul className="space-y-2 text-sm">
              <li><a href="/refund" data-testid="footer-refund" className="hover:text-[#6E1A24] transition-colors">{t("footer.refund")}</a></li>
              <li><a href="/terms" data-testid="footer-terms" className="hover:text-[#6E1A24] transition-colors">{t("footer.terms")}</a></li>
              <li><a href="/privacy" data-testid="footer-privacy" className="hover:text-[#6E1A24] transition-colors">{t("footer.privacy")}</a></li>
            </ul>
          </div>
        </div>

        <div className="pt-6 border-t border-[#F8F5F2]/15 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#F8F5F2]/50">
          <span>{(t("footer.copy") || "").replace("{year}", String(new Date().getFullYear()))}</span>
          <span>{t("footer.made")}</span>
        </div>
      </div>
    </footer>
  );
}
